import React from 'react';
import ArticleLayout from '../../components/articles/ArticleLayout';

const SecurityByDesignArticle = () => (
  <ArticleLayout articleId="security-by-design">
    <section>
      <h2>Security by Design: Building Trust Into Software Systems</h2>
      <p>
        Security by design treats security as a core engineering requirement, not a late-stage review. It shifts teams
        from “find and fix” to “prevent and control,” using architecture, defaults, and operational discipline to reduce
        risk continuously. Security is also a trust feature - customers experience it as uptime, no leaks, and predictable
        handling of access and privacy.
      </p>
      <blockquote>
        <p>A secure system is not one that never fails. It is one that fails safely, visibly, and recoverably.</p>
      </blockquote>
    </section>

    <hr />

    <section>
      <h3>What Security by Design Changes</h3>
      <p>Adding security at the end creates patchwork controls, logging gaps, and high-cost remediation. Designing it in delivers:</p>
      <ul>
        <li>Clear boundaries for data and access</li>
        <li>Repeatable secure patterns teams reuse</li>
        <li>Faster delivery with fewer late surprises</li>
        <li>Compliance readiness as a byproduct, not a scramble</li>
      </ul>
    </section>

    <section>
      <h3>Core Principles That Hold Up in Production</h3>
      <h4>Least privilege</h4>
      <p>Give users and services only the permissions they need, for only as long as needed (role-based access, short-lived credentials, separate admin actions).</p>
      <h4>Secure defaults</h4>
      <p>Systems should be safe even when teams forget to configure something - auth required on new endpoints, logging enabled, private storage by default.</p>
      <h4>Defense in depth</h4>
      <p>Assume controls fail; add network, auth, validation, secrets, monitoring, and backup layers.</p>
      <h4>Minimize attack surface</h4>
      <p>Remove unused routes, limit public APIs, restrict admin interfaces.</p>
      <h4>Make actions traceable</h4>
      <p>If you cannot answer “who did what and when,” you cannot investigate incidents or pass audits.</p>
    </section>

    <section>
      <h3>Threat Modeling: Prevent Expensive Mistakes Early</h3>
      <p>Threat modeling is a design tool, not a compliance ritual.</p>
      <ol>
        <li>Identify assets: customer data, credentials, payment flows, admin controls, source code.</li>
        <li>Identify actors: external attackers, malicious insiders, careless users, compromised partners.</li>
        <li>Map attack surfaces: public endpoints, uploads, webhooks, admin panels, CI/CD, cloud permissions.</li>
        <li>List likely threats: spoofing, tampering, disclosure, DoS, privilege escalation.</li>
        <li>Define mitigations: controls, monitoring, rate limits, validation, isolation.</li>
      </ol>
      <p>Run threat modeling per feature or workflow for best results.</p>
    </section>

    <section>
      <h3>Secure SDLC: Integrating Security into Delivery</h3>
      <h4>Requirements</h4>
      <ul>
        <li>Classify data (PII, financial, internal, public)</li>
        <li>Document regulatory constraints and security acceptance criteria</li>
      </ul>
      <h4>Design</h4>
      <ul>
        <li>Threat model major flows</li>
        <li>Define trust boundaries and choose secure patterns for auth, secrets, logging</li>
      </ul>
      <h4>Implementation</h4>
      <ul>
        <li>Enforce secure coding standards through review and tooling</li>
        <li>Keep access checks in the backend, not only UI</li>
      </ul>
      <h4>Testing</h4>
      <ul>
        <li>Automated security tests where possible</li>
        <li>Authorization and object-level access tests</li>
        <li>Dependency scanning and static analysis</li>
      </ul>
      <h4>Deployment & operations</h4>
      <ul>
        <li>Environment separation</li>
        <li>Secret rotation and access controls</li>
        <li>Monitoring, alerting, incident readiness</li>
      </ul>
    </section>

    <section>
      <h3>Identity and Access Management</h3>
      <h4>Authentication practices</h4>
      <ul>
        <li>MFA for admins and privileged accounts</li>
        <li>Strong password policies with modern hashing</li>
        <li>Secure session management, rotation, idle timeouts</li>
        <li>SSO options for enterprise customers</li>
      </ul>
      <h4>Authorization</h4>
      <ul>
        <li>Enforce tenant context on every request</li>
        <li>Object-level permission checks at APIs</li>
        <li>Separate read, write, export, admin actions</li>
      </ul>
      <h4>Service-to-service identity</h4>
      <p>Authenticate internal calls with mTLS or scoped tokens; rotate credentials frequently.</p>
    </section>

    <section>
      <h3>Data Protection: Confidentiality Without Fragility</h3>
      <h4>Classification and handling</h4>
      <p>Define public, internal, confidential, regulated categories with access, storage, logging, and retention rules.</p>
      <h4>Encryption and key management</h4>
      <ul>
        <li>TLS for data in transit; at-rest encryption where expected</li>
        <li>Centralized key management (KMS) with separation by environment/tenant</li>
      </ul>
      <h4>Secrets management</h4>
      <p>Keep secrets out of code, clients, logs; use secrets managers and automatic rotation.</p>
      <h4>Backups and recovery</h4>
      <ul>
        <li>Test restores regularly</li>
        <li>Protect backup access</li>
        <li>Define RTO/RPO expectations</li>
      </ul>
    </section>

    <section>
      <h3>Application Security Controls</h3>
      <p>Common risks: injection, broken access control, insecure direct object references, XSS, CSRF, SSRF, file upload issues.</p>
      <h4>Practical protections</h4>
      <ul>
        <li>Centralized input validation and schema checks</li>
        <li>Parameterized queries</li>
        <li>Outbound allowlists</li>
        <li>Content security policies for web apps</li>
        <li>Isolated storage and scanning for uploads</li>
        <li>Structured error responses without leaking internals</li>
      </ul>
    </section>

    <section>
      <h3>Dependency and Supply Chain Security</h3>
      <ul>
        <li>Automated vulnerability scanning in CI</li>
        <li>Lockfiles and reproducible builds</li>
        <li>Regular upgrade cadence</li>
        <li>Reviews for high-impact dependencies</li>
      </ul>
      <h4>CI/CD controls</h4>
      <ul>
        <li>Restrict pipeline changes</li>
        <li>Protect signing keys and deployment credentials</li>
        <li>Require review for production changes</li>
        <li>Log deployments and approvals</li>
      </ul>
    </section>

    <section>
      <h3>Cloud and Infrastructure Security</h3>
      <p>Most cloud failures stem from misconfigurations.</p>
      <ul>
        <li>Separate accounts/projects per environment</li>
        <li>MFA and conditional access for admins</li>
        <li>Network segmentation and private-by-default storage</li>
        <li>Infrastructure as code with policy checks</li>
        <li>Continuous patching for OS, containers, runtimes</li>
      </ul>
      <h4>Container/Kubernetes notes</h4>
      <ul>
        <li>Minimal base images, run as non-root</li>
        <li>Network policies and limited service accounts</li>
        <li>Protect cluster credentials</li>
      </ul>
    </section>

    <section>
      <h3>Logging, Monitoring, and Incident Response</h3>
      <h4>Log essentials</h4>
      <ul>
        <li>Authentication events</li>
        <li>Authorization failures</li>
        <li>Admin actions</li>
        <li>Data access for sensitive resources</li>
        <li>Integration events (webhooks, token use)</li>
      </ul>
      <h4>Monitoring</h4>
      <ul>
        <li>Unusual logins, location anomalies</li>
        <li>Spikes in authorization failures</li>
        <li>Rate limit or scraping patterns</li>
        <li>Data export volume anomalies</li>
        <li>Error spikes on critical endpoints</li>
      </ul>
      <h4>Incident readiness</h4>
      <ul>
        <li>Severity definitions and escalation paths</li>
        <li>On-call ownership</li>
        <li>Playbooks for credential compromise, data exposure, DDoS, ransomware</li>
        <li>Post-incident reviews with corrective actions</li>
      </ul>
    </section>

    <section>
      <h3>Security for SaaS and Multi-Tenant Systems</h3>
      <ul>
        <li>Establish tenant context at authentication</li>
        <li>Enforce scoping in every query and cache key</li>
        <li>Automated tests for cross-tenant access attempts</li>
        <li>Guarded admin tooling</li>
        <li>Audit logs with tenant identifiers</li>
      </ul>
      <p>Treat tenant isolation as a core invariant.</p>
    </section>

    <section>
      <h3>GenAI and Automation Security</h3>
      <ul>
        <li>Beware prompt injection via user or retrieved content</li>
        <li>Restrict retrieval sources by permission</li>
        <li>Treat retrieved content as untrusted input</li>
        <li>Validate outputs with schemas before actions</li>
        <li>Require approval for high-impact tool calls</li>
        <li>Redact sensitive data in prompts/logs</li>
      </ul>
    </section>

    <section>
      <h3>Security Metrics Worth Tracking</h3>
      <ul>
        <li>Time to remediate critical vulnerabilities</li>
        <li>Percentage of privileged users with MFA enabled</li>
        <li>Change failure rate tied to security releases</li>
        <li>Mean time to detect/respond to suspicious activity</li>
        <li>Incidents caused by misconfiguration</li>
        <li>Coverage of authorization tests</li>
      </ul>
    </section>

    <section>
      <h3>Security-by-Design Checklist</h3>
      <h4>Architecture and access</h4>
      <ul className="checklist">
        <li>Trust boundaries defined between users, services, tenants</li>
        <li>Object-level authorization enforced at API layer</li>
        <li>Least privilege roles for users and services</li>
        <li>Secure defaults for new endpoints/resources</li>
      </ul>
      <h4>Data protection</h4>
      <ul className="checklist">
        <li>Data classified with handling rules</li>
        <li>Encryption in transit and at rest</li>
        <li>Secrets stored in a proper secrets manager</li>
        <li>Backups tested with restore drills</li>
      </ul>
      <h4>Delivery and operations</h4>
      <ul className="checklist">
        <li>Dependency scanning and upgrade cadence</li>
        <li>CI/CD protected with reviews and restricted credentials</li>
        <li>Logging covers auth, admin, exports</li>
        <li>Monitoring for abnormal access patterns</li>
        <li>Incident playbooks and ownership defined</li>
      </ul>
    </section>

    <section>
      <h3>Closing Note</h3>
      <p>
        Security by design is not about adding friction. It is about building systems that can be trusted during normal
        use and under attack. Treat security as engineering - repeatable patterns, strong defaults, measurable outcomes,
        continuous improvement - and you reduce risk while keeping velocity.
      </p>
    </section>
  </ArticleLayout>
);

export default SecurityByDesignArticle;
